import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Alert, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiMessageSquare } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { updateSmsManagementRequest } from '../../store/actions/smsManagementActions';
import 'bootstrap/dist/css/bootstrap.css';
import { getSmsManagementById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import {
    parseSmsManagementResponse,
    normalizeProcessesFromApi,
    normalizeSmsMessagesFromApi,
} from '../../constants/smsManagement';
import SmsManagementForm from '../../components/Admin/SmsManagementForm';
import './AdminSmsEdit.css';

const AdminSmsEdit = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { smsManagementId } = useParams();
    const { update } = useSelector((state) => state.smsManagement);

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [initialProcesses, setInitialProcesses] = useState(['no_sms']);
    const [initialMessages, setInitialMessages] = useState(null);
    const [partnerName, setPartnerName] = useState('');
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        const fetchSmsManagement = async () => {
            try {
                setLoading(true);
                setLoadError(null);
                const data = await getSmsManagementById(smsManagementId);
                const { smsManagement: smsMgmt, user } = parseSmsManagementResponse(data);
                if (smsMgmt) {
                    setInitialProcesses(normalizeProcessesFromApi(smsMgmt.sms_processes));
                    setInitialMessages(normalizeSmsMessagesFromApi(smsMgmt.sms_messages));
                    setPartnerName(user?.company_name || user?.email || '');
                }
            } catch (e) {
                setLoadError(handleApiError(e, 'Erreur lors du chargement du SMS management'));
            } finally {
                setLoading(false);
            }
        };
        if (smsManagementId) fetchSmsManagement();
    }, [smsManagementId]);

    useEffect(() => {
        if (update?.success) {
            setSuccessMessage('SMS management modifié avec succès !');
            const reload = async () => {
                try {
                    const data = await getSmsManagementById(smsManagementId);
                    const { smsManagement: smsMgmt, user } = parseSmsManagementResponse(data);
                    if (smsMgmt) {
                        setInitialProcesses(normalizeProcessesFromApi(smsMgmt.sms_processes));
                        setInitialMessages(normalizeSmsMessagesFromApi(smsMgmt.sms_messages));
                        setPartnerName(user?.company_name || user?.email || '');
                    }
                } catch (e) {
                    console.error(handleApiError(e, 'Erreur lors du rechargement'));
                }
            };
            reload();
        }
        if (update?.error) {
            setSuccessMessage(null);
        }
    }, [update?.success, update?.error, smsManagementId]);

    const handleSubmit = (payload) => {
        dispatch(updateSmsManagementRequest(smsManagementId, payload));
    };

    return (
        <div className="dashboard-page admin-sms-edit-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h2 className="page-title mb-1">
                        <FiMessageSquare className="me-2" />
                        Modifier le SMS Management
                    </h2>
                    {partnerName && (
                        <p className="text-muted mb-0">{partnerName}</p>
                    )}
                </div>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    <FiArrowLeft className="me-2" /> Retour
                </Button>
            </div>

            {loadError && (
                <Alert variant="danger" className="mb-3">{loadError}</Alert>
            )}
            {successMessage && (
                <Alert variant="success" className="mb-3" dismissible onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                </Alert>
            )}

            <Card>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement…</p>
                        </div>
                    ) : (
                        <>
                            <SmsManagementForm
                                initialProcesses={initialProcesses}
                                initialMessages={initialMessages}
                                onSubmit={handleSubmit}
                                onCancel={() => navigate(-1)}
                                loading={update?.loading}
                                externalError={update?.error}
                                isUpdate
                                submitLabel={update?.loading ? 'Enregistrement…' : 'Enregistrer'}
                            />
                            {successMessage && (
                                <div className="d-flex justify-content-end mt-3">
                                    <Button variant="info" onClick={() => navigate(`/admin/sms/${smsManagementId}`)}>
                                        Voir les détails
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminSmsEdit;
